import React, { ReactNode, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Layout, times8 } from 'Constants/Styles';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Dropdown } from 'Components/Primitives/Dropdown/Dropdown';
import { ShowcaseAvatar } from 'Components/Primitives/Avatar/Avatar.types';
import { AvatarAndTitle } from 'Components/Primitives/Avatar/AvatarAndTitle';

interface AvatarDropDownProps {
  avatarList: ShowcaseAvatar[];
  children: ReactNode;
}

const DEFAULT_ITEM_HEIGHT = times8(5);

const StyledAvatarDropDown = styled.div`
  padding: 0 !important;
  .scroll-wrapper {
    padding: ${times8()}px;
    max-height: 300px;
    min-width: 300px;
    overflow: auto;
  }
`;

const StyledMenuListItem = styled.div<{ start: number }>`
  position: absolute;
  display: flex;
  align-items: center;
  padding: ${times8()}px;
  top: 0;
  left: 0;
  width: 100%;
  height: ${DEFAULT_ITEM_HEIGHT}px;
  transform: ${({ start }) => `translateY(${start}px)`};
  &:hover {
    background-color: ${Layout.backgroundColor};
  }
`;

export const AvatarDropDown: React.FC<AvatarDropDownProps> = ({ avatarList, children }) => {
  const setVisible = useState<boolean>()[1];
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: avatarList.length,
    getScrollElement: () => parentRef.current || null,
    estimateSize: () => DEFAULT_ITEM_HEIGHT
  });

  return (
    <Dropdown
      trigger={['hover']}
      arrow={true}
      onOpenChange={(o) => {
        setTimeout(() => {
          setVisible(o);
        }, 0);
      }}
      popupRender={() => (
        <StyledAvatarDropDown className="ant-dropdown-menu">
          <div ref={parentRef} className="scroll-wrapper">
            <div
              className="avatar-list"
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative'
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                const avatar = avatarList[virtualItem.index];
                return (
                  <StyledMenuListItem key={virtualItem.key} start={virtualItem.start}>
                    <AvatarAndTitle
                      data-testid="showcase-list-item-avatar"
                      className="showcase-list-item-avatar"
                      avatarSrc={avatar.avatarSrc}
                      title={avatar.title}
                      size="small"
                      linkTo={avatar.linkTo}
                    />
                  </StyledMenuListItem>
                );
              })}
            </div>
          </div>
        </StyledAvatarDropDown>
      )}
    >
      {children}
    </Dropdown>
  );
};
